import React, { useContext } from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import { gql } from "@apollo/client";
import { track, analyticsEvents } from "util/analytics";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import { useCurrentUser } from "hooks";
import NoOfEmployeesEngagementBox from "components/cards/NoOfEmployeesEngagementBox";
import SquarefootFeaturedListings from "components/SquarefootFeaturedListings";
import { memoize } from "lodash";
import Link from "next/link";
import { getSearchPageMetaData, GetMetaDataLinks } from "util/seo";
import Pagination from "components/Pagination";
import ListingCard from "./ListingCard";
import FlexibleSpaceCard from "./FlexibleSpaceCard";
import NarrowSearchBanner from "./NarrowSearchBanner";
import ZeroState from "./ZeroState";

import styles from "./styles.module.scss";

export const ES_LISTING_SEARCH_QUERY = gql`
  query publicListingNiceSearch(
    $groupByBuilding: Boolean
    $maxDaysOld: Int
    $minSqft: Int
    $maxSqft: Int
    $minOccupancy: Int
    $maxOccupancy: Int
    $minPricePerSquareFoot: Float
    $maxPricePerSquareFoot: Float
    $leaseTermMin: Int
    $leaseTermMax: Int
    $areaExternalIds: [UUID!]!
    $first: Int
    $after: String
    $minMonthlyPrice: Int
    $maxMonthlyPrice: Int
    $leaseTypes: [String!]
  ) {
    publicListingNiceSearch(
      groupByBuilding: $groupByBuilding
      maxDaysOld: $maxDaysOld
      minSqft: $minSqft
      maxSqft: $maxSqft
      minOccupancy: $minOccupancy
      maxOccupancy: $maxOccupancy
      minPsf: $minPricePerSquareFoot
      maxPsf: $maxPricePerSquareFoot
      minMonthlyPrice: $minMonthlyPrice
      maxMonthlyPrice: $maxMonthlyPrice
      leaseTermMinMonths: $leaseTermMin
      leaseTermMaxMonths: $leaseTermMax
      areaExternalIds: $areaExternalIds
      leaseTypes: $leaseTypes
    ) {
      listings(first: $first, after: $after) {
        nodes {
          id
          squareFeet
          estimatedSquareFeet
          maxOccupancy
          estimatedMaxOccupancy
          floorAndSuite
          estimatedMonthlyPrice
          accurateMonthlyPrice
          displayMonthlyPrice
          displayPSF
          status
          image2 {
            id
            url(size: "other_listing_small")
          }
          coverImage {
            id
            url(size: "other_listing_small")
          }
          leaseTerm
          leaseType
          description
          estimatedPsf
          accuratePsf
          building {
            id
            identity
            lat
            lng
            address
            slug
            city
            state
            zip
            neighborhood {
              id
              name
              parameterizedName
              yearlyPricePerFoot
            }
            submarket {
              id
              name
            }
            market {
              id
              name
            }
            hasVirtualTours
          }
          brokerInsights {
            content
          }
        }
        pageInfo {
          count
        }
      }
    }
  }
`;

const Card = ({
  listing,
  signedIn,
  handleListingClick,
  signUpType,
  source,
  onHoverListing,
}) => {
  const {
    values: {
      market,
      leaseTermMin,
      leaseTermMax,
      minPricePerSquareFoot,
      maxPricePerSquareFoot,
      minSqft,
      maxSqft,
      minMonthlyPrice,
      maxMonthlyPrice,
      numEmployees,
      neighborhoods,
      submarkets,
    },
  } = useContext(QueryManagerContext);
  const getLink = memoize(({ id, building }, isUserSignedIn) => {
    if (isUserSignedIn) {
      return `/tourbook/listing/${id}`;
    }
    return `/building${building.slug}/${building.identity}?listingId=${id}`;
  });

  return (
    <div
      className={styles.cardLink}
      key={`listing-${listing.id}`}
      onMouseEnter={() => onHoverListing(listing.id)}
    >
      <Link
        passHref
        href={getLink(listing, signedIn)}
        className={styles.listingCardLink}
        onClick={handleListingClick}
        data-qa="client-search-listing-card-link"
      >
        <a>
          <ListingCard
            signUpType={signUpType}
            source={source}
            signedIn={signedIn}
            className={styles.card}
            listing={listing}
            areaExternalIds={[
              ...neighborhoods.map(({ externalId }) => externalId),
              ...submarkets.map(({ externalId }) => externalId),
            ]}
            marketOfInterest={market?.externalId}
            leaseTermMin={leaseTermMin}
            leaseTermMax={leaseTermMax}
            minPricePerSquareFoot={minPricePerSquareFoot}
            maxPricePerSquareFoot={maxPricePerSquareFoot}
            minSqft={minSqft}
            maxSqft={maxSqft}
            minMonthlyPrice={minMonthlyPrice}
            maxMonthlyPrice={maxMonthlyPrice}
            numEmployees={numEmployees}
            phoneRequired={false}
            trackingEvent={() =>
              track(analyticsEvents.clicked, {
                category: "Public Search CTAs",
                label: "Bookmark",
              })
            }
            onSuccess={() => {}}
          />
        </a>
      </Link>
    </div>
  );
};
Card.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.string,
    building: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  signedIn: PropTypes.bool.isRequired,
  handleListingClick: PropTypes.func.isRequired,
  signUpType: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  onHoverListing: PropTypes.func.isRequired,
};

const Listings = ({
  className,
  handleListingClick,
  showNarrowSearchBanner,
  onHoverListing,
}) => {
  const { values, update } = useContext(QueryManagerContext);
  const { currentUser } = useCurrentUser();
  const metaDataLinks = GetMetaDataLinks(values.page);
  const {
    area,
    market,
    submarkets,
    neighborhoods,
    listings,
    count,
    page,
    limit,
  } = values;

  const onFlexCardLoginSignUp = () => {
    update({ leaseTermMax: 12 });
  };

  if (listings.length === 0) {
    return <ZeroState />;
  }
  const squarefootFeaturedListingsAreas = [
    ...neighborhoods.map(({ externalId }) => externalId),
    ...submarkets.map(({ externalId }) => externalId),
  ];
  if (squarefootFeaturedListingsAreas.length === 0)
    squarefootFeaturedListingsAreas.push(area);
  return (
    <>
      <Head>
        <link rel="canonical" href={metaDataLinks.canonical} />
        {page < Math.ceil(count / limit) && (
          <link rel="next" href={metaDataLinks.next} />
        )}
        {page > 1 && <link rel="prev" href={metaDataLinks.previous} />}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              getSearchPageMetaData(values.market, listings, [
                ...submarkets,
                ...neighborhoods,
              ])
            ),
          }}
        />
      </Head>
      <div className={className}>
        {/* <h2>{Math.floor(count / 10) * 10}+ listings available</h2> */}
        <div className={styles.container}>
          <SquarefootFeaturedListings
            mode="ice"
            areaUuids={squarefootFeaturedListingsAreas}
          />
          {listings.slice(0, 2).map((listing) => (
            <Card
              key={listing.id}
              listing={listing}
              signedIn={!!currentUser}
              handleListingClick={handleListingClick}
              signUpType="Public Search"
              source="Bookmark"
              onHoverListing={onHoverListing}
            />
          ))}
          {!currentUser && (
            <>
              <FlexibleSpaceCard
                className={styles.cardLink}
                onSignUp={onFlexCardLoginSignUp}
              />
              <div className={styles.shortCTA}>
                <NoOfEmployeesEngagementBox
                  market={market?.externalId}
                  areas={[...values.submarkets, ...values.neighborhoods]}
                  lead="Public Search"
                  source="External"
                />
              </div>
            </>
          )}
          {listings.slice(2).map((listing) => (
            <Card
              key={listing.id}
              listing={listing}
              signedIn={!!currentUser}
              handleListingClick={handleListingClick}
              signUpType="Public Search"
              source="Bookmark"
              onHoverListing={onHoverListing}
            />
          ))}
        </div>

        {showNarrowSearchBanner && count > 20 && (
          <NarrowSearchBanner listingCount={count - 20} />
        )}

        <Pagination
          position={page}
          paginationType="page"
          total={count}
          itemsPerPage={limit}
          getLink={(pageNumber, label) => (
            <span
              tabIndex={0}
              role="button"
              title={pageNumber}
              onClick={() => update({ page: pageNumber })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  update({ page: pageNumber });
                }
              }}
            >
              {label}
            </span>
          )}
        />
      </div>
    </>
  );
};

Listings.propTypes = {
  // Optional onClick handler for when a listing is clicked.
  // Useful for things like popping up a modal when a certain number of listings are clicked.
  handleListingClick: PropTypes.func,
  className: PropTypes.string,
  showNarrowSearchBanner: PropTypes.bool,
  onHoverListing: PropTypes.func,
};

Listings.defaultProps = {
  onHoverListing: () => {},
  handleListingClick: () => {},
  showNarrowSearchBanner: false,
  className: undefined,
};

export default Listings;
